import type { CatalogEntry } from '../types';

/**
 * Warmup (feature `03`) error codes + catalog.
 *
 * Code shape is `03AAEE` (decimal, zero-padded):
 *   - `AA` = surface within the feature (`00` = feature-level / cross-surface).
 *   - `EE` = specific condition within that surface.
 *
 * Warmup is driven by `apps/warmup-worker` (queue processors, not HTTP
 * controllers), so these codes are raised inside background evaluation/transition
 * logic and surface in worker logs / failed-job records rather than to an end
 * user. They still flow through `AppException` so the failure envelope and
 * server-side `logContext` are uniform with the rest of the platform.
 *
 * Surface → `AA` map:
 *   stateMachine=02   (StateMachineService transitions).
 *   interactions=03   (InteractionRandomizerService plan-config resolution).
 *   verification=04   (email-worker inbox-placement verification send/check).
 *   connection=05     (email-worker provider resolution / SMTP connect).
 *   AA=01 (poolEvaluation) is retired — PoolEnteredAtMissing was never thrown; the slot is left vacant.
 */
export const WARMUP_CODES = {
  /** State-machine transitions (AA=02). */
  StateMachine: {
    /** suspendAccount was called with a non-suspended target state. */
    InvalidSuspendState: '030201',
  },
  /** Interaction randomization (AA=03). */
  Interactions: {
    /** dispatchByType received an interaction type with no executor branch. */
    UnknownType: '030302',
  },
  /** Inbox-placement verification (AA=04) — email-worker send/check processors. */
  Verification: {
    /** The EmailAccount referenced by a verification-send job no longer exists. */
    AccountNotFound: '030401',
    /** No active seed accounts are configured, so no probe can be sent. */
    NoActiveSeeds: '030402',
    /** The InboxVerificationRun referenced by a verification-check job is missing. */
    RunNotFound: '030403',
    /** No seed checker is registered for the requested seed provider. */
    NoSeedChecker: '030404',
    /** A seed-checker request to the provider API failed (non-2xx). */
    SeedCheckRequestFailed: '030405',
  },
  /** Provider connection (AA=05) — adapter factory + SMTP connect. */
  Connection: {
    /** EmailProviderFactory received an unrecognized provider enum value. */
    UnknownProvider: '030501',
    /** SMTP connect was attempted with incomplete decrypted credentials. */
    SmtpCredentialsMissing: '030502',
  },
} as const;

export const WARMUP_CATALOG: Record<string, CatalogEntry> = {
  // ── stateMachine ──
  '030201': {
    httpStatus: 500,
    message: 'Warmup state transition could not be completed for this account.',
  },
  // ── interactions ──
  '030302': {
    httpStatus: 500,
    message: 'This warmup interaction type is not supported.',
  },
  // ── verification ──
  '030401': {
    httpStatus: 404,
    message: 'The email account for this verification run could not be found.',
  },
  '030402': {
    httpStatus: 500,
    message: 'No active seed accounts are configured for inbox-placement verification.',
  },
  '030403': {
    httpStatus: 404,
    message: 'The requested inbox verification run could not be found.',
  },
  '030404': {
    httpStatus: 500,
    message: 'No seed checker is available for the requested provider.',
  },
  '030405': {
    httpStatus: 502,
    message: 'A seed-account placement check could not be completed.',
  },
  // ── connection ──
  '030501': {
    httpStatus: 500,
    message: 'The email provider for this account is not supported.',
  },
  '030502': {
    httpStatus: 500,
    message: 'SMTP credentials for this account are incomplete.',
  },
};
